import { ApiQueryParams } from '@frontend/api-utils';
import { Event, EventQueryParams } from '@frontend/events/types';
import React, { ReactNode } from 'react';

import { HistoryEventDetails } from './history-event-details.component';
import { HistoryEventIcon } from './history-event-icon.component';
import { getHistoryEventTitleClassName, mapEventDataToDisplay } from './map-event-data-to-display';
import useTransactionHistory from './transaction-history.controller';

const ID = 'transaction-history';
export interface TransactionHistoryProps {
    transactionId?: string;
    entityType?: string;
    entityId?: string;
    pageSize?: number;
    showChildren?: boolean;
    title?: ReactNode;
    queryParams?: ApiQueryParams<EventQueryParams>;

    /**
     * @param event
     * if null is returned, the event will be rendered with the default UI
     */
    content?: (event: Event) => React.ReactNode | null;

    /**
     * Optional render prop for event reference badges shown in the default event UI.
     * Pass e.g. `(event) => <EventReferenceBadges event={event} />` from a caller
     * that can import from `@frontend/components/common` without creating a cycle.
     */
    renderEventBadges?: (event: Event) => React.ReactNode;
}

export const TransactionHistory = (props: TransactionHistoryProps) => {
    const viewProps = useTransactionHistory(props);

    if (!viewProps.events || viewProps.events.length === 0) {
        return;
    }

    return (
        <>
            {props.title && <p className='text-always-black justify-start text-base font-medium'>{props.title}</p>}
            <div
                id={ID}
                className='bg-always-white inline-flex w-full flex-col items-start self-stretch rounded-xl p-4 pt-6 pb-1'
                ref={viewProps.containerRef}>
                {viewProps.events.map((e, index) => (
                    <HistoryEvent
                        key={e.id}
                        event={e}
                        isLast={index === viewProps.events.length - 1}
                        isFirst={index === 0}
                        content={props.content}
                        renderEventBadges={props.renderEventBadges}
                    />
                ))}
            </div>
        </>
    );
};

export default TransactionHistory;

const HistoryEvent = (props: {
    event: Event;
    content?: (event: Event) => React.ReactNode | null;
    renderEventBadges?: (event: Event) => React.ReactNode;
    isLast?: boolean;
    isFirst?: boolean;
}) => {
    const content = props.content ? props.content(props.event) : null;
    return (
        <div className='group flex gap-x-6'>
            <HistoryEventIcon
                event={props.event}
                isLast={props.isLast}
                isFirst={props.isFirst}
            />
            <div className='text-always-black flex -translate-y-4 flex-col gap-2 pt-3 pb-4'>
                {content != null ? (
                    content
                ) : (
                    <>
                        <p className={getHistoryEventTitleClassName(props.event)}>{mapEventDataToDisplay(props.event.data)}</p>
                        <HistoryEventDetails
                            event={props.event}
                            timestamp={
                                <>
                                    {new Date(props.event.creation_timestamp).toLocaleDateString()}{' '}
                                    {new Date(props.event.creation_timestamp).toLocaleTimeString()}
                                </>
                            }
                        />
                        {props.renderEventBadges?.(props.event)}
                    </>
                )}
            </div>
        </div>
    );
};
